/**
 * Shifts — list of shifts with closeout report (totals, payment-method breakdown,
 * order count, void/force-close count) for each.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { useCashierNames } from '../useCashierNames.js';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { AlertTriangle, ChevronDown, ChevronRight, Pencil, X } from 'lucide-react';
import {
  DateRange,
  PageHeader,
  SegmentedControl,
  resolveDateRange,
  type DateRangeValue,
} from '../ui/index.js';

type ShiftRow = Database['public']['Tables']['shifts']['Row'];
type OrderRow = Database['public']['Tables']['orders']['Row'];
type PaymentRow = Database['public']['Tables']['payments']['Row'];
type ShiftExpenseRow = Database['public']['Tables']['shift_expenses']['Row'];

const EXPENSE_CATEGORIES: ShiftExpenseRow['category'][] = [
  'taxi',
  'supplies',
  'meals',
  'delivery',
  'other',
];

function cashAmt(s: ShiftSummary): number {
  return s.paymentsByMethod['cash'] ?? 0;
}
function cardAmt(s: ShiftSummary): number {
  return s.paymentsByMethod['card'] ?? 0;
}
function fmt(iso: string | null, locale: string, empty: string) {
  return iso
    ? new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })
    : empty;
}

const EXPENSE_I18N: Record<ShiftExpenseRow['category'], string> = {
  taxi: 'shifts.catTaxi',
  supplies: 'shifts.catSupplies',
  meals: 'shifts.catMeals',
  delivery: 'shifts.catDelivery',
  other: 'shifts.catOther',
};

interface ShiftSummary {
  shift: ShiftRow;
  orderCount: number;
  paidCount: number;
  voidCount: number;
  forcedCount: number;
  grossTotal: number;
  paymentsByMethod: Record<string, number>;
  expenses: ShiftExpenseRow[];
}

export function ShiftsView({
  clientId,
  focusShiftId,
  onConsumeFocus,
  onOpenOrders,
  role,
}: {
  clientId: string | null;
  focusShiftId: string | null;
  onConsumeFocus: () => void;
  onOpenOrders?: (shiftId: string) => void;
  role?: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const payMethodLabel = (m: string) =>
    m === 'cash' ? t('shifts.cash') : m === 'card' ? t('shifts.card') : m;
  const [summaries, setSummaries] = useState<ShiftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // date range filter — default: last 7 days
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekAgoStr = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  })();
  const [range, setRange] = useState<DateRangeValue>({ preset: '7d' });
  const { from: fromDate, to: toDate } = resolveDateRange(range);
  const [payFilter, setPayFilter] = useState<'all' | 'cash' | 'card'>('all');

  // pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  const cashierNames = useCashierNames(clientId);
  const cashierLabel = (id: number, userId?: string | null) => cashierNames.resolve(userId, id);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  // Reassign-cashier inline state: shiftId → selected profile UUID
  const [reassignId, setReassignId] = useState<string | null>(null);
  const [reassignValue, setReassignValue] = useState<string>('');
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);

  // Add-expense inline state: shiftId → form
  const [expenseShiftId, setExpenseShiftId] = useState<string | null>(null);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState<ShiftExpenseRow['category']>('other');
  const [expenseDesc, setExpenseDesc] = useState('');
  const [expenseBusy, setExpenseBusy] = useState(false);
  const [expenseError, setExpenseError] = useState<string | null>(null);

  async function saveReassign(shiftId: string) {
    if (!supabase || !reassignValue) return;
    const cashier = cashierNames.list.find((c) => c.id === reassignValue);
    if (!cashier || cashier.cashierNo == null) {
      setReassignError(t('shifts.reassignNoHdm'));
      return;
    }
    setReassignBusy(true);
    setReassignError(null);
    const { error: err } = await supabase
      .from('shifts')
      .update({ cashier_id: cashier.cashierNo, cashier_user_id: cashier.id })
      .eq('id', shiftId);
    setReassignBusy(false);
    if (err) {
      setReassignError(err.message);
      return;
    }
    // Update local state so the table reflects immediately without a full reload.
    setSummaries((prev) =>
      prev.map((s) =>
        s.shift.id === shiftId
          ? {
              ...s,
              shift: {
                ...s.shift,
                cashier_id: cashier.cashierNo!,
                cashier_user_id: cashier.id,
              },
            }
          : s,
      ),
    );
    setReassignId(null);
    setReassignValue('');
  }

  async function getCurrentUserName(): Promise<string> {
    if (!supabase) return 'unknown';
    const { data } = await supabase.auth.getUser();
    const u = data?.user;
    return u?.user_metadata?.full_name || u?.email || u?.id || 'unknown';
  }

  async function addExpense(shiftId: string) {
    if (!supabase) return;
    const amount = Number(expenseAmount);
    if (!amount || amount <= 0) {
      setExpenseError(t('shifts.errorPositive'));
      return;
    }
    if (!expenseDesc.trim()) {
      setExpenseError(t('shifts.errorDescription'));
      return;
    }
    const summary = summaries.find((s) => s.shift.id === shiftId);
    if (!summary) return;
    setExpenseBusy(true);
    setExpenseError(null);
    const by = await getCurrentUserName();
    const { data, error: err } = await supabase
      .from('shift_expenses')
      .insert({
        client_id: summary.shift.client_id,
        shift_id: shiftId,
        amount,
        category: expenseCategory,
        description: expenseDesc.trim(),
        created_by: by,
      })
      .select()
      .single();
    setExpenseBusy(false);
    if (err) {
      setExpenseError(err.message);
      return;
    }
    setSummaries((prev) =>
      prev.map((s) =>
        s.shift.id === shiftId ? { ...s, expenses: [...s.expenses, data as ShiftExpenseRow] } : s,
      ),
    );
    setExpenseShiftId(null);
    setExpenseAmount('');
    setExpenseCategory('other');
    setExpenseDesc('');
  }

  async function deleteExpense(shiftId: string, expenseId: string) {
    if (!supabase) return;
    await supabase.from('shift_expenses').delete().eq('id', expenseId);
    setSummaries((prev) =>
      prev.map((s) =>
        s.shift.id === shiftId
          ? { ...s, expenses: s.expenses.filter((e) => e.id !== expenseId) }
          : s,
      ),
    );
  }

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    let q = supabase.from('shifts').select('*').order('opened_at', { ascending: false }).limit(500);
    if (fromDate) q = q.gte('opened_at', new Date(fromDate).toISOString());
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      q = q.lte('opened_at', end.toISOString());
    }
    const { data: shifts, error: err } = await q;
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    if (!shifts || shifts.length === 0) {
      setSummaries([]);
      setLoading(false);
      return;
    }

    const shiftIds = shifts.map((s) => s.id);
    const [{ data: orders }, { data: payments }, { data: expenses }] = await Promise.all([
      supabase.from('orders').select('*').in('shift_id', shiftIds),
      supabase
        .from('payments')
        .select('id, order_id, method, amount, status, client_id, created_at'),
      supabase.from('shift_expenses').select('*').in('shift_id', shiftIds),
    ]);

    // Map order_id → shift_id
    const orderShift: Record<string, string> = {};
    (orders ?? []).forEach((o: OrderRow) => {
      if (o.shift_id) orderShift[o.id] = o.shift_id;
    });

    // Group payments by shift via order_id
    const paymentsByShift: Record<string, PaymentRow[]> = {};
    (payments ?? []).forEach((p) => {
      const sid = orderShift[p.order_id];
      if (!sid) return;
      (paymentsByShift[sid] ||= []).push(p as PaymentRow);
    });

    const summary: ShiftSummary[] = shifts.map((s) => {
      const sOrders = (orders ?? []).filter((o) => o.shift_id === s.id);
      const sPayments = (paymentsByShift[s.id] ?? []).filter((p) => p.status === 'paid');
      const paymentsByMethod: Record<string, number> = {};
      sPayments.forEach((p) => {
        paymentsByMethod[p.method] = (paymentsByMethod[p.method] ?? 0) + Number(p.amount);
      });
      return {
        shift: s,
        orderCount: sOrders.length,
        paidCount: sOrders.filter((o) => o.status === 'paid').length,
        voidCount: sOrders.filter((o) => o.status === 'void').length,
        forcedCount: sOrders.filter((o) => o.forced_close).length,
        grossTotal: sOrders
          .filter((o) => o.status === 'paid')
          .reduce((sum, o) => sum + Number(o.total), 0),
        paymentsByMethod,
        expenses: (expenses ?? []).filter((e) => e.shift_id === s.id) as ShiftExpenseRow[],
      };
    });

    setSummaries(summary);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page when date range or pay filter changes
  useEffect(() => {
    setPage(0);
  }, [range, payFilter]);

  const filteredSummaries = summaries.filter((s) => {
    if (payFilter === 'cash') return cashAmt(s) > 0;
    if (payFilter === 'card') return cardAmt(s) > 0;
    return true;
  });
  const periodCash = filteredSummaries.reduce((n, s) => n + cashAmt(s), 0);
  const periodPos = filteredSummaries.reduce((n, s) => n + cardAmt(s), 0);
  const periodOverall = filteredSummaries.reduce((n, s) => n + s.grossTotal, 0);

  // When navigated from the Orders view, expand and scroll to the focused shift.
  useEffect(() => {
    if (!focusShiftId || loading) return;
    if (!summaries.some((s) => s.shift.id === focusShiftId)) return;
    setExpanded(focusShiftId);
    const row = rowRefs.current[focusShiftId];
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onConsumeFocus();
  }, [focusShiftId, loading, summaries, onConsumeFocus]);

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('shifts.title')}
        subtitle={t('shifts.subtitle', { count: filteredSummaries.length })}
      />

      <div className="panel" style={{ marginBottom: 0 }}>
        <div className="add-form" style={{ padding: '10px 16px', flexWrap: 'wrap', gap: 12 }}>
          <DateRange value={range} onChange={setRange} size="sm" />
          <SegmentedControl
            size="sm"
            ariaLabel={t('shifts.paymentMethod')}
            value={payFilter}
            onChange={setPayFilter}
            options={[
              { value: 'all', label: t('shifts.all') },
              { value: 'cash', label: t('shifts.cash') },
              { value: 'card', label: t('shifts.card') },
            ]}
          />
        </div>
      </div>

      {!loading && filteredSummaries.length > 0 && (
        <div className="ui-stats">
          <div className="ui-stat">
            <div className="ui-stat-label">{t('shifts.overallTotal')}</div>
            <div className="ui-stat-value">{AMD(periodOverall)}</div>
          </div>
          <div className="ui-stat">
            <div className="ui-stat-label">{t('shifts.cashTotal')}</div>
            <div className="ui-stat-value">{AMD(periodCash)}</div>
          </div>
          <div className="ui-stat">
            <div className="ui-stat-label">{t('shifts.cardTotal')}</div>
            <div className="ui-stat-value">{AMD(periodPos)}</div>
          </div>
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      <div className="panel">
        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : filteredSummaries.length === 0 ? (
          <div className="empty-state">{t('shifts.empty')}</div>
        ) : (
          <table className="table table-cards">
            <thead>
              <tr>
                <th></th>
                <th>{t('shifts.colOpened')}</th>
                <th>{t('shifts.colClosed')}</th>
                <th>{t('shifts.colCashier')}</th>
                <th>{t('shifts.colOrders')}</th>
                <th>{t('shifts.colVoid')}</th>
                <th>{t('shifts.colForced')}</th>
                <th>{t('shifts.colTotal')}</th>
                <th>{t('shifts.colCash')}</th>
                <th>{t('shifts.colCard')}</th>
                <th>{t('shifts.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSummaries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((s) => (
                <>
                  <tr
                    key={s.shift.id}
                    ref={(el) => {
                      rowRefs.current[s.shift.id] = el;
                    }}
                    className={focusShiftId === s.shift.id ? 'highlight-row' : undefined}
                  >
                    <td className="table-cards-title">
                      <button
                        className="btn-icon"
                        onClick={() => setExpanded(expanded === s.shift.id ? null : s.shift.id)}
                        title={t('shifts.showBreakdown')}
                      >
                        {expanded === s.shift.id ? <ChevronDown className="ui-i" /> : <ChevronRight className="ui-i" />}
                      </button>
                      <span className="table-cards-phone-only">
                        {cashierLabel(s.shift.cashier_id, s.shift.cashier_user_id)}
                      </span>
                    </td>
                    <td data-label={t('shifts.colOpened')} style={{ color: 'var(--text-muted)' }}>{fmt(s.shift.opened_at, locale, t('common.emptyDash'))}</td>
                    <td data-label={t('shifts.colClosed')} style={{ color: 'var(--text-muted)' }}>{fmt(s.shift.closed_at, locale, t('common.emptyDash'))}</td>
                    <td className="table-cards-hide" data-label={t('shifts.colCashier')}>
                      {cashierLabel(s.shift.cashier_id, s.shift.cashier_user_id)}
                    </td>
                    <td data-label={t('shifts.colOrders')}>{s.orderCount}</td>
                    <td data-label={t('shifts.colVoid')}>{s.voidCount}</td>
                    <td data-label={t('shifts.colForced')} style={{ color: s.forcedCount > 0 ? 'var(--danger)' : undefined }}>
                      {s.forcedCount}
                    </td>
                    <td data-label={t('shifts.colTotal')}>{AMD(s.grossTotal)}</td>
                    <td data-label={t('shifts.colCash')}>{AMD(cashAmt(s))}</td>
                    <td data-label={t('shifts.colCard')}>{AMD(cardAmt(s))}</td>
                    <td data-label={t('shifts.colStatus')}>
                      <span
                        style={{
                          color: s.shift.status === 'open' ? 'var(--accent)' : 'var(--text-muted)',
                          fontWeight: 600,
                        }}
                      >
                        {s.shift.status === 'open' ? t('shifts.statusOpen') : t('shifts.statusClosed')}
                      </span>
                    </td>
                  </tr>
                  {expanded === s.shift.id && (
                    <tr key={`${s.shift.id}-detail`}>
                      <td colSpan={11} style={{ background: 'var(--bg-muted, #fafafa)' }}>
                        <div className="detail-grid">
                          <div>
                            <strong>{t('shifts.shiftId')}</strong>
                            <code style={{ fontSize: 11 }}>{s.shift.id}</code>
                          </div>
                          <div>
                            <strong>{t('shifts.openingFloat')}</strong> {AMD(s.shift.opening_float)}
                          </div>
                          <div>
                            <strong>{t('shifts.paidOrders')}</strong>{' '}
                            {t('shifts.paidOf', { paid: s.paidCount, total: s.orderCount })}
                          </div>
                          <div>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={() => onOpenOrders?.(s.shift.id)}
                            >
                              {t('shifts.viewOrders')}
                            </button>
                          </div>
                          {s.shift.status === 'closed' && (
                            <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                              {reassignId === s.shift.id ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <select
                                    value={reassignValue}
                                    onChange={(e) => setReassignValue(e.target.value)}
                                    style={{ fontSize: 13, padding: '4px 8px' }}
                                  >
                                    <option value="">{t('shifts.selectCashier')}</option>
                                    {cashierNames.list.map((c) => (
                                      <option key={c.id} value={c.id}>
                                        {c.name}
                                        {c.cashierNo != null
                                          ? t('shifts.cashierHdm', { no: c.cashierNo })
                                          : t('shifts.cashierNoHdm')}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    className="btn-sm btn-primary"
                                    disabled={reassignBusy || !reassignValue}
                                    onClick={() => void saveReassign(s.shift.id)}
                                  >
                                    {reassignBusy ? t('common.saving') : t('common.save')}
                                  </button>
                                  <button
                                    className="btn-ghost btn-sm"
                                    disabled={reassignBusy}
                                    onClick={() => {
                                      setReassignId(null);
                                      setReassignError(null);
                                    }}
                                  >
                                    {t('common.cancel')}
                                  </button>
                                  {reassignError && (
                                    <span style={{ color: 'var(--danger)', fontSize: 12 }}>
                                      {reassignError}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                role !== 'manager' && (
                                  <button
                                    className="btn-ghost btn-sm"
                                    onClick={() => {
                                      setReassignId(s.shift.id);
                                      setReassignValue(s.shift.cashier_user_id ?? '');
                                      setReassignError(null);
                                    }}
                                  >
                                    <Pencil className="ui-i" /> {t('shifts.reassign')}
                                  </button>
                                )
                              )}
                            </div>
                          )}
                        </div>
                        <h4 style={{ marginTop: 12 }}>{t('shifts.paymentBreakdown')}</h4>
                        {Object.keys(s.paymentsByMethod).length === 0 ? (
                          <p style={{ color: 'var(--text-subtle)' }}>{t('shifts.noPayments')}</p>
                        ) : (
                          <table className="table" style={{ maxWidth: 400 }}>
                            <thead>
                              <tr>
                                <th>{t('shifts.colMethod')}</th>
                                <th>{t('shifts.colTotal')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(s.paymentsByMethod).map(([m, total]) => (
                                <tr key={m}>
                                  <td>{payMethodLabel(m)}</td>
                                  <td>{AMD(total)}</td>
                                </tr>
                              ))}
                              {(() => {
                                const paymentsTotal = Object.values(s.paymentsByMethod).reduce(
                                  (a, b) => a + b,
                                  0,
                                );
                                const gap = s.grossTotal - paymentsTotal;
                                return (
                                  <>
                                    <tr>
                                      <td>
                                        <strong>{t('shifts.grandTotal')}</strong>
                                      </td>
                                      <td>
                                        <strong>{AMD(paymentsTotal)}</strong>
                                      </td>
                                    </tr>
                                    {Math.abs(gap) > 0 && (
                                      <tr>
                                        <td
                                          colSpan={2}
                                          style={{
                                            color: 'var(--text-muted)',
                                            fontSize: 12,
                                            paddingTop: 4,
                                          }}
                                        >
                                          <AlertTriangle className="ui-i" />{' '}
                                          {t('shifts.unrecorded', {
                                            amount: AMD(gap),
                                            orderTotal: AMD(s.grossTotal),
                                          })}
                                        </td>
                                      </tr>
                                    )}
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>
                        )}

                        {/* ── Shift Expenses ── */}
                        <h4 style={{ marginTop: 16 }}>{t('shifts.expenses')}</h4>
                        {s.expenses.length === 0 ? (
                          <p style={{ color: 'var(--text-muted)' }}>{t('shifts.noExpenses')}</p>
                        ) : (
                          <table className="table" style={{ maxWidth: 500 }}>
                            <thead>
                              <tr>
                                <th>{t('shifts.colCategory')}</th>
                                <th>{t('shifts.colDescription')}</th>
                                <th>{t('shifts.colAddedBy')}</th>
                                <th>{t('shifts.colAmount')}</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.expenses.map((exp) => (
                                <tr key={exp.id}>
                                  <td>{t(EXPENSE_I18N[exp.category])}</td>
                                  <td style={{ color: 'var(--text-muted)' }}>
                                    {exp.description ?? t('common.emptyDash')}
                                  </td>
                                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                    {exp.created_by ?? t('common.emptyDash')}
                                  </td>
                                  <td>{AMD(exp.amount)}</td>
                                  <td>
                                    {role !== 'manager' && (
                                      <button
                                        className="btn-ghost btn-sm"
                                        style={{ color: 'var(--danger)' }}
                                        onClick={() => void deleteExpense(s.shift.id, exp.id)}
                                      >
                                        <X className="ui-i" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td>
                                  <strong>{t('shifts.total')}</strong>
                                </td>
                                <td></td>
                                <td></td>
                                <td>
                                  <strong>
                                    {AMD(s.expenses.reduce((sum, e) => sum + Number(e.amount), 0))}
                                  </strong>
                                </td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        )}

                        {/* Add expense form */}
                        {expenseShiftId === s.shift.id ? (
                          <div
                            style={{
                              marginTop: 8,
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap',
                              alignItems: 'flex-end',
                            }}
                          >
                            <div>
                              <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                                {t('shifts.amountAmd')}
                              </label>
                              <input
                                className="input"
                                type="number"
                                min="0"
                                step="any"
                                placeholder="0"
                                value={expenseAmount}
                                onChange={(e) => setExpenseAmount(e.target.value)}
                                style={{ width: 100 }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                                {t('shifts.colCategory')}
                              </label>
                              <select
                                className="input"
                                value={expenseCategory}
                                onChange={(e) =>
                                  setExpenseCategory(e.target.value as ShiftExpenseRow['category'])
                                }
                              >
                                {EXPENSE_CATEGORIES.map((c) => (
                                  <option key={c} value={c}>
                                    {t(EXPENSE_I18N[c])}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={{ flex: '1 1 180px' }}>
                              <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                                {t('shifts.colDescription')}
                              </label>
                              <input
                                className="input"
                                placeholder={t('shifts.descriptionPlaceholder')}
                                value={expenseDesc}
                                onChange={(e) => setExpenseDesc(e.target.value)}
                              />
                            </div>
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => void addExpense(s.shift.id)}
                              disabled={expenseBusy || !expenseAmount || !expenseDesc.trim()}
                            >
                              {expenseBusy ? t('common.saving') : t('common.add')}
                            </button>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={() => {
                                setExpenseShiftId(null);
                                setExpenseError(null);
                              }}
                            >
                              {t('common.cancel')}
                            </button>
                            {expenseError && (
                              <span style={{ color: 'var(--danger)', fontSize: 13 }}>
                                {expenseError}
                              </span>
                            )}
                          </div>
                        ) : (
                          (() => {
                            const isLatestShift = summaries[0]?.shift.id === s.shift.id;
                            const canAdd = role !== 'manager' || isLatestShift;
                            if (!canAdd) return null;
                            return (
                              <button
                                className="btn-ghost btn-sm"
                                style={{ marginTop: 6 }}
                                onClick={() => {
                                  setExpenseShiftId(s.shift.id);
                                  setExpenseAmount('');
                                  setExpenseCategory('other');
                                  setExpenseDesc('');
                                  setExpenseError(null);
                                }}
                              >
                                {t('shifts.addExpense')}
                              </button>
                            );
                          })()
                        )}

                        {/* Cash summary */}
                        {s.expenses.length > 0 && (s.paymentsByMethod['cash'] ?? 0) > 0 && (
                          <div
                            style={{
                              marginTop: 12,
                              padding: '8px 12px',
                              background: 'var(--surface-raised)',
                              borderRadius: 6,
                              fontSize: 13,
                              maxWidth: 320,
                            }}
                          >
                            <strong>{t('shifts.expectedCash')}</strong>
                            <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>
                              {t('shifts.expectedFormula', {
                                cashSales: AMD(s.paymentsByMethod['cash'] ?? 0),
                                expenses: AMD(s.expenses.reduce((sum, e) => sum + Number(e.amount), 0)),
                                result: AMD(
                                  (s.paymentsByMethod['cash'] ?? 0) -
                                    s.expenses.reduce((sum, e) => sum + Number(e.amount), 0),
                                ),
                              })}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
        {filteredSummaries.length > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px 12px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              className="btn-ghost btn-sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('common.prev')}
            </button>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {t('common.pageOf', {
                page: page + 1,
                count: Math.ceil(filteredSummaries.length / PAGE_SIZE),
              })}
            </span>
            <button
              className="btn-ghost btn-sm"
              disabled={(page + 1) * PAGE_SIZE >= filteredSummaries.length}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
